import { FC, memo, ReactNode, useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useScrollIndicators } from '@robocorp/components';
import { styled } from '@robocorp/theme';

import { BreadcrumbItem, BreadcrumbContext } from '~/hooks';
import { TopNavigation } from '../appBar';
import { SideNavigationConnected } from '../side-navigation';

const Main = styled.main<{ isCollapsed: boolean }>`
  background: ${({ theme }) => theme.colors.background.primary.color};
  height: 100%;
  display: grid;
  grid-template-columns: ${({ isCollapsed }) => (isCollapsed ? 0 : 240)}px 1fr;
  grid-template-rows: auto 1fr;
  grid-template-areas: 'aside header' 'aside section';
  ${({ theme }) => theme.screen.m} {
    grid-template-columns: 1fr;
    grid-template-areas: 'header' 'section';
  }

  > header {
    grid-area: header;
  }

  > section {
    grid-area: section;
    padding: 2px calc(5rem - var(--scrollbar-width)) 3rem 5rem;
    display: flex;
    flex-direction: column;

    ${({ theme }) => theme.screen.m} {
      padding: 0 ${({ theme }) => theme.space.$32};
    }

    ${({ theme }) => theme.screen.s} {
      padding: 0 ${({ theme }) => theme.space.$16};
    }
  }
`;

const Section = styled.section`
  overflow: auto;
`;

const Content: FC<{ onToggle: () => void; children: ReactNode }> = memo(
  ({ children, onToggle }) => {
    const contentRef = useScrollIndicators('top');

    return (
      <>
        <TopNavigation onToggle={onToggle} />
        <Section ref={contentRef}>{children}</Section>
      </>
    );
  },
);

export const AppLayout: FC<{ children: ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const breadcrumbContext = useState<BreadcrumbItem[]>([]);
  const { pathname } = useLocation();
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);

  const onClose = useCallback(() => setIsOpen(false), []);
  const onToggle = useCallback(() => setIsOpen((currentIsOpen) => !currentIsOpen), []);

  useEffect(() => {
    if (isOpen) {
      setIsOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  /**
   * Toggle main sidebar visibillity to permanent or visible only on mouse hover
   */
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === '[') {
        const target = e.target as HTMLElement;

        // Do not toggle sidebar if ctrl/command key is pressed or an input field is active
        if (
          e.ctrlKey ||
          e.metaKey ||
          target.isContentEditable ||
          ['INPUT', 'TEXTAREA', 'BUTTON'].indexOf(target?.tagName || '') > -1
        ) {
          return;
        }

        e.preventDefault();
        setIsCollapsed((curr) => !curr);
      }
    };

    window.addEventListener('keydown', onKeyDown, { passive: true });

    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  return (
    <Main isCollapsed={isCollapsed}>
      <BreadcrumbContext.Provider value={breadcrumbContext}>
        <SideNavigationConnected open={isOpen} onClose={onClose} isCollapsed={isCollapsed} />
        <Content onToggle={onToggle}>{children}</Content>
      </BreadcrumbContext.Provider>
    </Main>
  );
};
